import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { getCoreRowModel } from '@tanstack/react-table';

// MUI Theme
import { Paper } from '@mui/material';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';

// Services
import { exportEquipmentPairings, getEquipmentPairings } from '../../../services/equipmentpairing.service';

// Types
import { SearchBarFilterTypes } from '../../../constants/Constants';
import { FilterableTypes } from '../../../types/FilterableTypes';

// Utils
import { Storage } from '../../../utils/Storage';
import { FormatForApi, shortDateFormat } from '../../../utils/Format';

// Common Components
import { useStandardTable as useTable, Table } from '../../common/table/index';
import FormInputCollection from '../../common/FormInputCollection/FormInputCollection';
import SearchBar from '../../common/SearchBar/SearchBar';

// Components
import makePairingHistoryColumn from '../device/makePairingHistoryColumn';
import PairingVerificationStatusTabs from './components/PairingVerificationStatusTabs';

// Hooks
import { fetchAllSettings } from '../../../redux/slices/settingsSlice';
import { useAppDispatch, useAppSelector } from '../../../redux/hook';
import { useExport } from '../../../hooks/useExport';

export default function PairingHistoryList(): JSX.Element {
  const [paramFilter, setParamFilter] = useState<any>({ entityType: 'EquipmentPairing', filters: [] });
  const [pairedDateTimeFrom, setPairedDateTimeFrom] = useState<Date | null>(null);
  const [pairedDateTimeTo, setPairedDateTimeTo] = useState<Date | null>(null);
  const [unpairedDateTimeFrom, setUnpairedDateTimeFrom] = useState<Date | null>(null);
  const [unpairedDateTimeTo, setUnpairedDateTimeTo] = useState<Date | null>(null);
  const [modifiedDateTimeFrom, setModifiedDateTimeFrom] = useState<Date | null>(null);
  const [modifiedDateTimeTo, setModifiedDateTimeTo] = useState<Date | null>(null);
  const [pairingStatusToggles, setPairingStatusToggles] = useState<number[]>([]);
  const [isResetting, setIsResetting] = useState(false);

  const dispatch = useAppDispatch();
  const settingStates = useAppSelector((state) => state.settings);

  const { isExporting, startExport } = useExport('EquipmentPairing');

  useEffect(() => {
    if (isResetting) {
      // Reset paramFilter and clear the resetting flag
      setParamFilter({ entityType: 'EquipmentPairing', filters: [], sortBy: paramFilter.sortBy });
      setIsResetting(false);
    }
  }, [isResetting, paramFilter.sortBy]);

  useEffect(() => {
    dispatch(fetchAllSettings());
    const filterState = Storage.getSearchBarFiltering(SearchBarFilterTypes.PAIRING_HISTORY);
    if (filterState) {
      setParamFilter({
        ...paramFilter,
        filters: filterState.filters,
      });
    }
  }, []);

  const columns = useMemo(() => makePairingHistoryColumn(), []);

  const table = useTable<any>({
    columns,
    fetchAsync: getEquipmentPairings,
    exportAsync: exportEquipmentPairings,
    params: paramFilter,
    serverSide: true,
  });

  const onSearchChange = (filters: any[]) => {
    const finalFilters = [...filters];

    const fnCheckDateTimes = (filterName, from, to) => {
      let fromFilter;
      let toFilter;
      if (from) {
        fromFilter = {};
        fromFilter[filterName] = FormatForApi(moment(from), true);
        fromFilter.filterType = FilterableTypes.GreaterThanOrEqual;

        if (to) {
          fromFilter.and = {};
          fromFilter.and[filterName] = FormatForApi(moment(to), false);
          fromFilter.and.filterType = FilterableTypes.LessThanOrEqual;
        }

        finalFilters.push(fromFilter);
      } else if (to) {
        toFilter = {};
        toFilter[filterName] = moment(to).endOf('day').format('YYYY-MM-DD HH:mm');
        toFilter.filterType = FilterableTypes.LessThanOrEqual;

        finalFilters.push(toFilter);
      }
    };

    fnCheckDateTimes('pairedDateTime', pairedDateTimeFrom, pairedDateTimeTo);
    fnCheckDateTimes('unpairedDateTime', unpairedDateTimeFrom, unpairedDateTimeTo);
    fnCheckDateTimes('modifiedDateTime', modifiedDateTimeFrom, modifiedDateTimeTo);

    setParamFilter({ ...paramFilter, entityType: 'EquipmentPairing', filters: finalFilters });
    Storage.setSearchBarFiltering(SearchBarFilterTypes.PAIRING_HISTORY, { isMoreFilter: true, filters: finalFilters });
  };

  const handleFilterToggle = (type: 'pairingValidationStatus', selectedValues: string[] | number[]) => {
    const filterValue = selectedValues.join('|');

    const updatedFilters = (paramFilter.filters || []).filter((filter: any) => {
      if (type === 'pairingValidationStatus') {
        return !('pairingValidationStatus' in filter);
      }
      return true;
    });

    const newFilters = filterValue ? [{ [type]: filterValue, filterType: FilterableTypes.Contains }] : [];

    if (type === 'pairingValidationStatus') {
      setPairingStatusToggles(selectedValues as number[]);
    }

    const combinedFilters = [...updatedFilters, ...newFilters];

    setParamFilter({ entityType: 'EquipmentPairing', filters: combinedFilters, sortBy: paramFilter.sortBy });
    Storage.setSearchBarFiltering(SearchBarFilterTypes.DEVICES, {
      isMoreFilter: true,
      filters: combinedFilters,
    });
  };

  const handleReset = () => {
    setIsResetting(true);
    setPairedDateTimeFrom(null);
    setPairedDateTimeTo(null);
    setUnpairedDateTimeFrom(null);
    setUnpairedDateTimeTo(null);
    setModifiedDateTimeFrom(null);
    setModifiedDateTimeTo(null);
    setPairingStatusToggles([]);
    setParamFilter({ entityType: 'EquipmentPairing', filters: [], sortBy: paramFilter.sortBy });
  };

  return (
    <>
      <SearchBar
        pageKey={SearchBarFilterTypes.PAIRING_HISTORY}
        onSearch={onSearchChange}
        columns={columns}
        allowSearchAll={true}
        onReset={handleReset}
        components={
          <div>
            <div style={{ display: 'flex', flexWrap: 'wrap', marginBottom: 5 }}>
              <PairingVerificationStatusTabs
                pairingStatusToggles={pairingStatusToggles}
                settingStates={settingStates}
                handlePairingStatusToggle={(event, newValues) => handleFilterToggle('pairingValidationStatus', newValues)}
              />
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <FormInputCollection label='Paired' style={{ marginTop: '4px', marginLeft: '24px', marginRight: '24px' }}>
                  <div style={{ marginRight: '8px' }}>
                    <DatePicker
                      label='From'
                      value={pairedDateTimeFrom ? moment(pairedDateTimeFrom) : null}
                      format={shortDateFormat}
                      sx={{ width: '185px' }}
                      slotProps={{ textField: { size: 'small' } }}
                      onChange={(date) => {
                        setPairedDateTimeFrom(date ? date.toDate() : null);
                      }}
                    />
                  </div>
                  <DatePicker
                    label='To'
                    value={pairedDateTimeTo ? moment(pairedDateTimeTo) : null}
                    format={shortDateFormat}
                    sx={{ width: '185px' }}
                    slotProps={{ textField: { size: 'small' } }}
                    onChange={(date) => {
                      setPairedDateTimeTo(date ? date.toDate() : null);
                    }}
                  />
                </FormInputCollection>
                <FormInputCollection label='Unpaired' style={{ marginTop: '4px', marginRight: '24px' }}>
                  <DatePicker
                    value={unpairedDateTimeFrom}
                    label='From'
                    format={shortDateFormat}
                    slotProps={{
                      textField: { size: 'small', style: { width: '165px', marginRight: '10px' } },
                      actionBar: { actions: ['clear'] },
                    }}
                    onChange={(date) => {
                      setUnpairedDateTimeFrom(date);
                    }}
                  />
                  <DatePicker
                    value={unpairedDateTimeTo}
                    label='To'
                    format={shortDateFormat}
                    slotProps={{
                      textField: { size: 'small', style: { width: '165px' } },
                      actionBar: { actions: ['clear'] },
                    }}
                    onChange={(date) => {
                      setUnpairedDateTimeTo(date);
                    }}
                  />
                </FormInputCollection>
                <FormInputCollection label='Modified' style={{ marginTop: '4px', marginRight: '24px' }}>
                  <DatePicker
                    label='From'
                    value={modifiedDateTimeFrom ? moment(modifiedDateTimeFrom) : null}
                    format={shortDateFormat}
                    slotProps={{
                      textField: { size: 'small', style: { width: '165px', marginRight: '10px' } },
                      actionBar: { actions: ['clear'] },
                    }}
                    onChange={(date) => setModifiedDateTimeFrom(date ? date.toDate() : null)}
                  />
                  <DatePicker
                    label='To'
                    value={modifiedDateTimeTo ? moment(modifiedDateTimeTo) : null}
                    format={shortDateFormat}
                    slotProps={{
                      textField: { size: 'small', style: { width: '165px' } },
                      actionBar: { actions: ['clear'] },
                    }}
                    onChange={(date) => {
                      setModifiedDateTimeTo(date ? date.toDate() : null);
                    }}
                  />
                </FormInputCollection>
              </LocalizationProvider>
            </div>
          </div>
        }
      />
      <Paper>
        <Table
          {...table}
          isExporting={isExporting}
          canExportData={() => true}
          exportData={() => startExport(paramFilter.filters ?? [])}
          showSearchBar={false}
          showColumnToggler={false}
          getCoreRowModel={getCoreRowModel()}
        />
      </Paper>
    </>
  );
}
